import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { testimonialService } from '../services/api';

// Testimonials section content
const testimonialsContent = {
  title: 'What our',
  titleHighlight: 'customers say',
  subtitle: 'Real stories from partners and learners who accelerated their growth with Service Hub.'
};

const Section = styled.section`
  background: linear-gradient(180deg, ${p => p.theme.colors.gray[100]} 0%, #fff 100%);
  padding: ${p => p.theme.spacing.xl} 0 ${p => p.theme.spacing['2xl']};

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: ${p => p.theme.spacing.lg} 0 ${p => p.theme.spacing.xl};
  }
`;

const Container = styled.div`
  max-width: ${p => p.theme.breakpoints.wide};
  margin: 0 auto;
  padding: 0 ${p => p.theme.spacing.lg};

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: 0 ${p => p.theme.spacing.md};
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${p => p.theme.spacing.xl};

  h2 {
  font-size: ${p => p.theme.fontSizes['3xl']};
    color: ${p => p.theme.colors.dark};
    margin-bottom: ${p => p.theme.spacing.md};

    .highlight {
      color: ${p => p.theme.colors.primary};
    }
  }

  p {
    color: ${p => p.theme.colors.gray[600]};
    max-width: 760px;
    margin: 0 auto;
    font-size: ${p => p.theme.fontSizes.base};
  }
`;

/*
  Revamped layout: a deck with one focused card in the middle and
  2 side cards behind it (scaled, blurred, and faded) to mimic the
  reference screenshot.
*/

const Stage = styled.div`
  position: relative;
  height: 400px; /* allow room for larger avatar */
  max-width: 1024px; /* slightly narrower */
  margin: 0 auto;
  perspective: 1000px; /* tiny 3D feel for subtle depth */
  cursor: pointer; /* allow click-to-navigate on left/right halves */
  touch-action: pan-y; /* allow vertical scrolling but intercept horizontal swipes */
  user-select: none; /* prevent text selection during swipe */

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    height: 360px;
    max-width: 92vw;
    cursor: grab;
    
    &:active {
      cursor: grabbing;
    }
  }
`;

const DeckItem = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  transition: transform 500ms ease, opacity 400ms ease, filter 400ms ease, box-shadow 400ms ease;
  will-change: transform, opacity, filter;
`;

const CardShell = styled.div`
  /* banner height variable so the avatar locks to the seam */
  --banner-h: 90px; /* reduced */
  width: clamp(240px, 54vw, 480px); /* smaller card width */
  /* Fixed height to ensure all cards are the same size */
  min-height: 380px; /* slightly taller to accommodate bigger avatar */
  max-height: 380px;
  /* stronger rounding on the bottom as requested */
  border-radius: 20px 20px 56px 56px;
  background: #fff;
  box-shadow: 0 14px 44px rgba(0,0,0,0.14);
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    min-height: 340px;
    max-height: 340px;
  }
`;

const Banner = styled.div`
  height: var(--banner-h);
  background: ${p => p.theme.colors.primary};
  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    height: 88px;
  }
`;

const Avatar = styled.div`
  position: absolute;
  top: var(--banner-h); /* center on the banner/body seam */
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120px; /* increased avatar size */
  height: 120px;
  border-radius: ${p => p.theme.borderRadius.full};
  background: ${p => p.theme.colors.gray[200]};
  overflow: hidden;
  display: grid;
  place-items: center;
  color: ${p => p.theme.colors.dark};
  font-weight: ${p => p.theme.fontWeights.bold};
  box-shadow: 0 8px 16px rgba(0,0,0,0.16);
  border: 6px solid #fff; /* ring to make it pop over split */

  img { width: 100%; height: 100%; object-fit: cover; }
  
  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    top: 88px;
    width: 100px;
    height: 100px;
  }
`;

const Body = styled.div`
  padding: 76px ${p => p.theme.spacing.md} ${p => p.theme.spacing.md};
  text-align: center;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: 64px ${p => p.theme.spacing.md} ${p => p.theme.spacing.md};
  }
`;

const Name = styled.h3`
  margin: 0 0 ${p => p.theme.spacing.xs};
  color: ${p => p.theme.colors.dark};
  font-size: ${p => p.theme.fontSizes.lg};
`;

const Role = styled.p`
  margin: 0 0 ${p => p.theme.spacing.md};
  color: ${p => p.theme.colors.gray[600]};
  font-size: ${p => p.theme.fontSizes.xs};
`;

const Quote = styled.p`
  margin: 0 auto;
  color: ${p => p.theme.colors.gray[700]};
  line-height: 1.6;
  font-size: ${p => p.theme.fontSizes.sm};
  max-width: 56ch;
  /* Limit quote to 6 lines with ellipsis for overflow */
  display: -webkit-box;
  -webkit-line-clamp: 4; /* reduced to balance larger avatar */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    -webkit-line-clamp: 3;
    font-size: ${p => p.theme.fontSizes.xs};
  }
`;

const Brand = styled.div`
  margin-top: ${p => p.theme.spacing.lg};
  color: ${p => p.theme.colors.gray[800]};
  font-weight: ${p => p.theme.fontWeights.semibold};
`;

const Carousel = styled.div`
  position: relative;
`;

const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const total = testimonials.length;

  // Touch/swipe handling for mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      next();
    } else if (isRightSwipe) {
      prev();
    }
  };

  // Load testimonials
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const data = await testimonialService.getFeaturedTestimonials();
        setTestimonials(data || []);
      } catch (error) {
        // eslint-disable-next-line no-console
        // Set empty array on error
        setTestimonials([]);
      }
    };
    fetchTestimonials();
  }, []);

  const next = useCallback(() => setIndex(i => (i + 1) % total), [total]);
  const prev = useCallback(() => setIndex(i => (i - 1 + total) % total), [total]);

  // Optional autoplay with pause on hover
  const [paused, setPaused] = useState(false);
  const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  useEffect(() => {
    if (paused || prefersReduced) return;
    const id = setInterval(next, 6000);
    return () => clearInterval(id);
  }, [paused, next, prefersReduced]);

  // distance resolver for circular deck
  const getDiff = useCallback((i) => {
    if (!total) return 0;
    let d = i - index;
    const half = Math.floor(total / 2);
    if (d > half) d -= total;
    if (d < -half) d += total;
    return d;
  }, [index, total]);

  return (
    <Section>
      <Container>
        <Header>
          <h2>
            {testimonialsContent.title} <span className="highlight">{testimonialsContent.titleHighlight}</span>
          </h2>
          <p>{testimonialsContent.subtitle}</p>
        </Header>

        <Carousel onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <Stage
            role="region"
            aria-label="Testimonials"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'ArrowRight') next(); if (e.key === 'ArrowLeft') prev(); }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              if (x < rect.width / 2) {
                prev();
              } else {
                next();
              }
            }}
          >
            {testimonials.map((t, i) => {
              const d = getDiff(i);
              // tuned parameters for side cards: wide spread, minimal tilt, mostly visible
              const baseX = 200; // tighter spread between cards on desktop
              const x = d * baseX;
              const abs = Math.abs(d);
              const scale = d === 0 ? 1 : 0.9; // neighbors smaller but equal size
              const rotate = 0; // no tilt for background cards
              const z = d === 0 ? 3 : (abs === 1 ? 2 : 1);
              const opacity = d === 0 ? 1 : (abs === 1 ? 0.7 : 0.35);
              const blur = 0; // keep text readable on background cards

              // Keep only immediate neighbors visible for symmetry and equal sizing
              const hidden = abs > 1;

              return (
                <DeckItem
                  key={i}
                  aria-hidden={d !== 0}
                  aria-roledescription="slide"
                  aria-current={d === 0}
                  onClick={(ev) => {
                    // Click directly on a side card selects it
                    if (d !== 0) {
                      ev.stopPropagation();
                      setIndex(i);
                    }
                  }}
                  style={{
                    zIndex: z,
                    opacity: hidden ? 0 : opacity,
                    filter: hidden ? 'blur(3px)' : `blur(${blur}px)`,
                    transform: `translateX(calc(-50% + ${x}px)) scale(${scale}) rotate(${rotate}deg)`
                  }}
                >
                  <CardShell>
                    <Banner />
                    <Avatar>
                      {t.avatarUrl ? (
                        <img src={t.avatarUrl} alt={`${t.name} avatar`} />
                      ) : (
                        (t.name || 'S').split(' ').map(s => s[0]).join('').slice(0,2).toUpperCase()
                      )}
                    </Avatar>
                    <Body>
                      <Name>{t.name}</Name>
                      {t.role ? <Role>{t.role}</Role> : null}
                      <Quote>“{t.quote}”</Quote>
                      {t.company ? <Brand>{t.company}</Brand> : null}
                    </Body>
                  </CardShell>
                </DeckItem>
              );
            })}
          </Stage>
        </Carousel>
      </Container>
    </Section>
  );
};

export default Testimonials;
